// Calculation and API service for retirement calculator
import { forEach } from 'lodash-es'
import { capitalize } from 'vue'
import RetCalcService from '@/services/RetCalcService'
import type { RetirementData, IncomeAsset, AssumptionValue, RetirementCalculatorPayload } from './types'

export const useRetirementCalculationService = () => {
  
  const validateCriticalFields = (userData: RetirementData, partnerData: RetirementData, enablePartnerForm: boolean): void => {
    const errors: string[] = []
    
    if (userData.contribution_rate === null || userData.contribution_rate === undefined) {
      errors.push('User contribution rate (userSG) is required')
    }
    
    if (enablePartnerForm && (partnerData.contribution_rate === null || partnerData.contribution_rate === undefined)) {
      errors.push('Partner contribution rate (partnerSG) is required when partner form is enabled')
    }
    
    if (errors.length > 0) {
      throw new Error(`Critical fields missing for retirement calculation: ${errors.join(', ')}`)
    }
  }

  const buildCalculationPayload = (
    userData: RetirementData,
    partnerData: RetirementData,
    incomeAssets: IncomeAsset[],
    assumptions: {
      insurancePremium: number
      annualAdminFees: number
      assumptionValues: AssumptionValue[]
      inflation: number
      wageGrowth: number
      noAgePension: boolean
    },
    enablePartnerForm: boolean
  ): RetirementCalculatorPayload => {
    
    // Validate critical fields before building payload
    validateCriticalFields(userData, partnerData, enablePartnerForm)
    
    // Convert incomes to annual
    let income = userData.income || 0
    let partnerIncome = partnerData.income || 0
    
    // Convert user income to annual
    if (userData.income_time_period === 'weekly') {
      income = userData.income * 52
    } else if (userData.income_time_period === 'fortnightly') {
      income = userData.income * 26
    } else if (userData.income_time_period === 'monthly') {
      income = userData.income * 12
    }
    
    // Convert partner income to annual using partner's time period
    if (enablePartnerForm && partnerIncome) {
      if (partnerData.income_time_period === 'weekly') {
        partnerIncome = partnerIncome * 52
      } else if (partnerData.income_time_period === 'fortnightly') {
        partnerIncome = partnerIncome * 26
      } else if (partnerData.income_time_period === 'monthly') {
        partnerIncome = partnerIncome * 12
      }
      // If yearly or undefined, use as-is
    }

    // Convert contributions to annual
    const bTaxCont = userData.before_tax_frequency === 'yearly'
      ? userData.before_tax_contribution
      : (userData.before_tax_contribution || 0) * 12

    const pBTaxCont = enablePartnerForm && partnerData.before_tax_frequency === 'yearly'
      ? partnerData.before_tax_contribution
      : enablePartnerForm ? (partnerData.before_tax_contribution || 0) * 12 : null

    const aTaxCont = userData.after_tax_frequency === 'yearly'
      ? userData.after_tax_contribution
      : (userData.after_tax_contribution || 0) * 12

    const pATaxCont = partnerData.after_tax_frequency === 'yearly'
      ? partnerData.after_tax_contribution
      : (partnerData.after_tax_contribution || 0) * 12

    const payload: RetirementCalculatorPayload = {
      userGAS: income,
      userSAB: userData.current_super_balance,
      userDesiredRet: userData.desired_retirement_age,
      desiredRetIncome: userData.desired_retirement_income,
      insurancePremiums: assumptions.insurancePremium,
      superFee: assumptions.annualAdminFees,
      investmentAssumptions: [...assumptions.assumptionValues],
      inflation: assumptions.inflation,
      wageGrowth: assumptions.wageGrowth,
      userSG: userData.contribution_rate,
      age: userData.age,
      additionalCont: bTaxCont,
      userBeforeTaxFrequency: userData.before_tax_frequency,
      afterTaxCont: aTaxCont,
      oneOffContribution: { 
        age: userData.one_off_age, 
        amount: userData.one_off_contribution 
      },
      hasHome: userData.hasHome,
      hasPartner: enablePartnerForm,
      partnerAge: partnerData.age || undefined,
      partnerGAS: enablePartnerForm ? partnerIncome : undefined,
      partnerSAB: partnerData.current_super_balance || undefined,
      partnerSG: enablePartnerForm ? partnerData.contribution_rate : undefined,
      partnerRetAge: partnerData.desired_retirement_age || undefined,
      pAdditionalCont: enablePartnerForm ? pBTaxCont : undefined,
      partnerBeforeTaxFrequency: partnerData.before_tax_frequency || undefined,
      pAfterTaxCont: enablePartnerForm ? pATaxCont : undefined,
      pOneOffContribution: enablePartnerForm ? {
        age: partnerData.one_off_age,
        amount: partnerData.one_off_contribution,
      } : undefined,
      investments: [],
      noAgePension: assumptions.noAgePension,
      careerBreak: userData.career_breaks,
      pCareerBreak: partnerData.career_breaks,
      partTime: {
        age: userData.part_time.part_time_starting_age,
        duration: userData.part_time.part_time_duration,
        daysWorking: userData.part_time.part_time_days,
      },
      pPartTime: {
        age: partnerData.part_time.part_time_starting_age,
        duration: partnerData.part_time.part_time_duration,
        daysWorking: partnerData.part_time.part_time_days,
      },
    }

    // Process income assets
    if (incomeAssets && incomeAssets.length > 0) {
      forEach(incomeAssets, (el: IncomeAsset) => {
        if (el.type) {
          let income = 0
          if (el.type !== 'home') {
            const multiplier = el.frequency === 'monthly' ? 1
              : el.frequency === 'yearly' ? 1 / 12
              : el.frequency === 'fortnightly' ? 26 / 12
              : 52 / 12
            income = (el.income || 0) * multiplier
          }
          
          const elName = el.type !== 'cashAndFixed' ? capitalize(el.type) : 'Cash'
          const investIndex = payload.investments.findIndex((investment) => {
            return investment.type === el.type
          })
          
          if (investIndex >= 0) {
            payload.investments[investIndex].monthly_income += income
            payload.investments[investIndex].value += el.current_value || 0
          } else {
            payload.investments.push({
              name: elName,
              type: el.type,
              regular_deposit_amount_to_invest: 0,
              reinvesting_whats_left: 0,
              monthly_income: income,
              monthly_expenses: 0,
              value: el.current_value || 0,
              using_to_support_retirement: 1,
              owner: 'single',
            })
          }
        }
      })
    }

    return payload
  }

  const performCalculation = async (payload: RetirementCalculatorPayload) => {
    return await RetCalcService.postRetirementCalculatorData(payload)
  }

  const downloadPDF = async (pdfPayload: any) => {
    return await RetCalcService.downloadPDF(pdfPayload)
  }

  const fetchPreservationAges = async () => {
    return await RetCalcService.getPreservationAgesForRetCalc()
  }

  const getPreservationAgeForBirthDate = async (birthDate: string, isUser: boolean) => {
    // This method calculates preservation age based on birth date
    // For Australian superannuation, preservation age varies by birth date
    const birthYear = new Date(birthDate).getFullYear()
    
    let preservationAge = 60 // Default for those born after July 1, 1964
    
    if (birthYear < 1960) {
      preservationAge = 55
    } else if (birthYear >= 1960 && birthYear < 1961) {
      preservationAge = 56
    } else if (birthYear >= 1961 && birthYear < 1962) {
      preservationAge = 57
    } else if (birthYear >= 1962 && birthYear < 1963) {
      preservationAge = 58
    } else if (birthYear >= 1963 && birthYear < 1964) {
      preservationAge = 59
    }
    
    return preservationAge
  }

  return {
    validateCriticalFields,
    buildCalculationPayload,
    performCalculation,
    downloadPDF,
    fetchPreservationAges,
    getPreservationAgeForBirthDate,
  }
}